import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Script from 'next/script';

import config from '../../../config';
import { usePathname } from 'next/navigation';
import { rewriteImgixToImageCdn } from '../../constants/imageCdn';

const SITE_URL = config.baseUri;

const Meta = ({
  title = 'Togather',
  subtitle = 'Book Unique Street Food Caterers',
  description = 'Discover, compare and easily book high quality street food caterers for your event.',
  image = 'https://images.prismic.io/togather-public/6865703a-ad7c-42a1-837c-eb4a7755f79b_Meta+Image.png',
  path = '',
  type = 'website',
  averageRating = 4.9,
  schemaType = 'LocalBusiness',
  reviewCount = 680,
  noIndexNoFollow = false,
}) => {
  const pathname = usePathname();
  const url = path ?? `${SITE_URL}/listings${pathname}`;
  const formattedTitle = subtitle ? `${subtitle} | ${title}` : title;
  const formattedImage = rewriteImgixToImageCdn(image);

  const reviewSchema = `
    {
      "@context": "http://schema.org",
      "@type": "${schemaType}",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "${averageRating}",
        "reviewCount": "${reviewCount}"
      },
      "name": "${subtitle}",
      "description": "${description}",
      "url": "${url}",
      ${
        schemaType !== 'Product'
          ? `"currenciesAccepted": "GBP",
            "paymentAccepted": "Credit Card, Debit Card, PayPal",
            "openingHours": "Mo-Su",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "London",
              "addressCountry": "GB",
              "postalCode": "E1 5JL",
              "streetAddress": "68-80 Hanbury Street"
            },`
          : ''
      }
      "sameAs": [
        "https://www.facebook.com/togathereventsuk",
        "https://twitter.com/togatherevents",
        "https://uk.linkedin.com/company/togatherevents",
        "https://www.instagram.com/togatherevents_/"
      ],
      "image": [
        "${formattedImage}"
      ]
      ${
        schemaType === 'Product'
          ? `,
        "brand": {
          "@type": "Service",
          "name": "Togather"
        }
      `
          : ''
      }
    }
  `;

  const shouldHaveNoIndexNoFollow = config.noIndexNoFollow || noIndexNoFollow;

  return (
    <>
      <Head>
        <title>{formattedTitle}</title>
        {shouldHaveNoIndexNoFollow && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <meta name="description" content={description} />

        <meta property="og:title" content={formattedTitle} />

        <meta property="og:description" content={description} />

        <meta property="og:image" content={formattedImage} />

        <meta property="og:type" content={type} />

        <meta property="og:url" content={url} />

        <meta
          name="facebook-domain-verification"
          content="rhl9vedef2uwjuhubv5kcxzmddp1kf"
        />

        <link rel="canonical" href={url} />

        <link rel="alternate" hrefLang="en-gb" href={url} />
      </Head>

      {config.enableSchemas && (
        <Script
          id="structured-data-script"
          key={`structured-data-script-${url}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewSchema }}
        />
      )}
    </>
  );
};

Meta.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  averageRating: PropTypes.number,
  reviewCount: PropTypes.number,
  schemaType: PropTypes.oneOf(['Product', 'LocalBusiness']).isRequired,
  noIndexNoFollow: PropTypes.bool,
};

export default Meta;
